import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Phone,
  Mail,
  MessageSquare,
  Send,
  CalendarDays,
  PawPrint,
  Baby,
  Sparkles,
  AlertCircle,
  Info,
} from "lucide-react";

const FORMSUBMIT_ENDPOINT = "https://formsubmit.co/ajax/wilhelm.martel@orange.fr";
const PHONE = "+33664104815";
const PHONE_DISPLAY = "+33 6 64 10 48 15";
const TOWEL_PRICE = 6;

const todayStr = () => new Date().toISOString().split("T")[0];

const ContactFormSectionEN = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    checkin: "",
    checkout: "",
    guests: "",
    pet: "no",
    petCount: "",
    towels: false,
    babyCot: false,
    babyChair: false,
    name: "",
    email: "",
    phone: "",
    message: "",
    _honey: "",
  });
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const nights = useMemo(() => {
    if (!formData.checkin || !formData.checkout) return 0;
    const diff = (new Date(formData.checkout) - new Date(formData.checkin)) / 86400000;
    return diff > 0 ? Math.round(diff) : 0;
  }, [formData.checkin, formData.checkout]);

  const towelEstimate = useMemo(() => {
    if (!formData.towels || !formData.guests) return 0;
    return Number(formData.guests) * TOWEL_PRICE;
  }, [formData.towels, formData.guests]);

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  };

  const validate = () => {
    const e = {};
    if (!formData.checkin) e.checkin = "Please enter your arrival date";
    if (!formData.checkout) e.checkout = "Please enter your departure date";
    if (formData.checkin && formData.checkout && nights <= 0)
      e.checkout = "Departure must be after arrival";
    else if (formData.checkin && formData.checkout && nights < 2)
      e.checkout = "Minimum stay: 2 nights";
    if (!formData.guests) e.guests = "Please enter the number of guests";
    if (!formData.name.trim()) e.name = "Your name is required";
    if (!formData.email.trim()) e.email = "Your email is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._honey) return;
    if (!validate()) return;

    setStatus("sending");

    const babyEquip = [];
    if (formData.babyCot) babyEquip.push("Baby cot");
    if (formData.babyChair) babyEquip.push("High chair");

    const payload = {
      "Name": formData.name,
      email: formData.email,
      "Phone": formData.phone || "Not provided",
      "Arrival": formatDate(formData.checkin),
      "Departure": formatDate(formData.checkout),
      "Nights": String(nights),
      "Guests": formData.guests,
      "Pet":
        formData.pet === "yes"
          ? "Yes" + (formData.petCount ? ` (${formData.petCount})` : "")
          : "No",
      "Towels": formData.towels
        ? `Yes — €${towelEstimate} (2 towels/person, €6/person for the stay)`
        : "No",
      "Baby equipment": babyEquip.length ? babyEquip.join(", ") : "No",
      "Message": formData.message || "—",
      _subject: "New stay enquiry — Cœurs de Normands",
      _template: "table",
      _captcha: "false",
    };

    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && (data.success === "true" || data.success === true)) {
        navigate("/en/thank-you");
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  const inputBase =
    "w-full px-4 py-3 pl-12 text-base border rounded-lg focus:ring-2 focus:ring-[#1C3A27] focus:border-[#1C3A27] transition-colors";
  const errClass = (f) => (errors[f] ? "border-red-400" : "border-[#EDE6DB]");

  return (
    <section
      id="contact-form"
      className="section-padding bg-gradient-to-br from-[#FAF9F6] to-[#EDE6DB]/30"
      aria-labelledby="contact-form-title"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <h2
            id="contact-form-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4"
          >
            Check availability
          </h2>
          <p className="text-lg text-[#1C3A27]/70 max-w-2xl mx-auto leading-relaxed">
            Tell us your dates and needs. Nathalie and Wilhelm will reply to you directly.
          </p>
          <p className="mt-3 text-sm text-[#1C3A27]/60 max-w-2xl mx-auto">
            Send us your stay request: we'll check availability and get back to you shortly. A request is not a confirmed booking.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-xl border border-[#EDE6DB]/50">
          {status === "error" && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-red-800 text-sm">
                Something went wrong. You can try again or contact us directly at{" "}
                <a href={`tel:${PHONE}`} className="font-semibold underline">
                  {PHONE_DISPLAY}
                </a>
                .
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" noValidate>
            <input
              type="text"
              name="_honey"
              value={formData._honey}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />

            {/* Block 1 — Your stay */}
            <fieldset>
              <legend className="font-serif text-xl font-bold text-[#1C3A27] mb-4">Your stay</legend>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="checkin" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Arrival *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="checkin"
                      name="checkin"
                      value={formData.checkin}
                      onChange={handleChange}
                      min={todayStr()}
                      className={`${inputBase} ${errClass("checkin")}`}
                    />
                    <CalendarDays size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.checkin && <p className="text-red-600 text-xs mt-1">{errors.checkin}</p>}
                </div>
                <div>
                  <label htmlFor="checkout" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Departure *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="checkout"
                      name="checkout"
                      value={formData.checkout}
                      onChange={handleChange}
                      min={formData.checkin || todayStr()}
                      className={`${inputBase} ${errClass("checkout")}`}
                    />
                    <CalendarDays size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.checkout && <p className="text-red-600 text-xs mt-1">{errors.checkout}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="guests" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Number of guests *
                  </label>
                  <div className="relative">
                    <select
                      id="guests"
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      className={`${inputBase} appearance-none bg-white ${errClass("guests")}`}
                    >
                      <option value="">Select</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n}>
                          {n} {n > 1 ? "guests" : "guest"}
                        </option>
                      ))}
                    </select>
                    <Users size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.guests && <p className="text-red-600 text-xs mt-1">{errors.guests}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Travelling with a pet?
                  </label>
                  <div className="flex gap-3 items-center">
                    <div className="relative flex-1">
                      <select
                        name="pet"
                        value={formData.pet}
                        onChange={handleChange}
                        className={`${inputBase} appearance-none bg-white border-[#EDE6DB]`}
                        aria-label="Travelling with a pet"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                      <PawPrint size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                    </div>
                    {formData.pet === "yes" && (
                      <input
                        type="number"
                        name="petCount"
                        min="1"
                        max="5"
                        value={formData.petCount}
                        onChange={handleChange}
                        placeholder="How many?"
                        className="w-28 px-3 py-3 text-base border border-[#EDE6DB] rounded-lg focus:ring-2 focus:ring-[#1C3A27] focus:border-[#1C3A27]"
                        aria-label="Number of pets"
                      />
                    )}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Block 2 — Options */}
            <fieldset>
              <legend className="font-serif text-xl font-bold text-[#1C3A27] mb-4">Options</legend>
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-4 rounded-lg border border-[#EDE6DB] hover:bg-[#EDE6DB]/20 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="towels"
                    checked={formData.towels}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-[#1C3A27] flex-shrink-0"
                  />
                  <span className="flex items-center gap-2 flex-wrap">
                    <Sparkles size={18} className="text-[#1C3A27]" />
                    <span className="font-medium text-[#1C3A27]">I would like to hire towels</span>
                    <span className="block w-full text-sm text-[#1C3A27]/60">
                      2 towels per guest — €6 per guest for the entire stay
                      {towelEstimate > 0 && (
                        <span className="ml-1 font-semibold text-[#1C3A27]">• Estimated: €{towelEstimate}</span>
                      )}
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3 p-4 rounded-lg border border-[#EDE6DB] hover:bg-[#EDE6DB]/20 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="babyCot"
                    checked={formData.babyCot}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-[#1C3A27] flex-shrink-0"
                  />
                  <span className="flex items-center gap-2">
                    <Baby size={18} className="text-[#1C3A27]" />
                    <span className="font-medium text-[#1C3A27]">I'll need a baby cot</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 p-4 rounded-lg border border-[#EDE6DB] hover:bg-[#EDE6DB]/20 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="babyChair"
                    checked={formData.babyChair}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-[#1C3A27] flex-shrink-0"
                  />
                  <span className="flex items-center gap-2">
                    <Baby size={18} className="text-[#1C3A27]" />
                    <span className="font-medium text-[#1C3A27]">I'll need a high chair</span>
                  </span>
                </label>
              </div>

              <div className="mt-4 flex items-start gap-2 text-sm text-[#1C3A27]/70 bg-[#EDE6DB]/30 rounded-lg p-3">
                <Info size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  Bed linen included • Beds made on arrival • Minimum stay 2 nights • Mandatory cleaning: €70
                </span>
              </div>
            </fieldset>

            {/* Block 3 — Your details */}
            <fieldset>
              <legend className="font-serif text-xl font-bold text-[#1C3A27] mb-4">Your details</legend>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Full name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`${inputBase} ${errClass("name")}`}
                      placeholder="Your full name"
                    />
                    <Users size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`${inputBase} ${errClass("email")}`}
                      placeholder="your@email.com"
                    />
                    <Mail size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="phone" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                  Phone
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    inputMode="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`${inputBase} border-[#EDE6DB]`}
                    placeholder="+33 6 12 34 56 78"
                  />
                  <Phone size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="message" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                  Additional information
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 pl-12 text-base border border-[#EDE6DB] rounded-lg focus:ring-2 focus:ring-[#1C3A27] focus:border-[#1C3A27] transition-colors resize-none"
                    placeholder="A question, a special request or anything we should know?"
                  />
                  <MessageSquare size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                </div>
              </div>
            </fieldset>

            {formData.checkin && formData.checkout && formData.guests && (
              <div className="bg-[#1C3A27]/5 border border-[#1C3A27]/10 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#1C3A27] mb-3">Summary of your enquiry</h3>
                <ul className="text-sm text-[#1C3A27]/80 space-y-1">
                  <li>
                    <strong>From {formatDate(formData.checkin)} to {formatDate(formData.checkout)}</strong>
                    {nights >= 2 && <span> • {nights} nights</span>}
                  </li>
                  <li>{formData.guests} guest{Number(formData.guests) > 1 ? "s" : ""}</li>
                  <li>Pet: {formData.pet === "yes" ? "yes" : "no"}</li>
                  <li>Towels: {formData.towels ? `yes${towelEstimate ? ` (≈ €${towelEstimate})` : ""}` : "no"}</li>
                  {(formData.babyCot || formData.babyChair) && (
                    <li>
                      Baby equipment:{" "}
                      {[formData.babyCot && "baby cot", formData.babyChair && "high chair"]
                        .filter(Boolean)
                        .join(", ")}
                    </li>
                  )}
                </ul>
              </div>
            )}

            <div className="text-center pt-2">
              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-primary text-lg px-8 py-4 min-w-[240px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Sending your enquiry…</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center space-x-2">
                    <Send size={20} />
                    <span>Send my enquiry</span>
                  </span>
                )}
              </button>
              <p className="text-xs text-[#1C3A27]/60 mt-3">
                You are not paying anything now — this is simply an availability enquiry.
              </p>
            </div>
          </form>
        </div>

        <div className="mt-12 text-center">
          <p className="text-[#1C3A27]/70 mb-6">Or book directly on our partner platforms:</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://www.airbnb.fr/rooms/1124426465764074190"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-8 py-3 w-full sm:w-auto"
            >
              Book on Airbnb
            </a>
            <a
              href="https://www.booking.com/hotel/fr/gite-au-coeur-de-la-suisse-normande.fr.html"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-8 py-3 w-full sm:w-auto"
            >
              Book on Booking
            </a>
            <a href={`tel:${PHONE}`} className="btn-secondary px-8 py-3 w-full sm:w-auto">
              Call
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSectionEN;
